<script setup>
import { safeBraindateTarget, appendBraindateSso } from '#shared/utilities/braindate/link';
import { modal_button_outline_2026 } from '~~/shared/utilities/ui';

const config = useRuntimeConfig();
const { braindateUrl } = config.public;
const route = useRoute();
const { loggedIn, user } = useUserSession();

// `to` arrives as a query param after the login round trip, so it is
// attacker-controllable — validate the origin before appending the SSO pair,
// which carries the visitor's email. appendBraindateSso no-ops when the session
// has no braindateHash, so the link still resolves somewhere useful rather than
// leaving the visitor on a spinner that never resolves.
const href = computed(() => {
    if (!loggedIn.value || !user.value) return null;
    const target = safeBraindateTarget(route.query.to, `${braindateUrl}/`);
    return appendBraindateSso(target, user.value);
});

// Hands off automatically (UNB-693, Nick 2026-08-19). This briefly rendered a
// button instead, on the reasoning that a NEW TAB opened after the login round
// trip cannot survive a popup blocker — the user gesture is long gone by then.
// That holds, but only for a new tab. Navigating THIS tab has no such
// restriction, and leaving the site is the accepted outcome here: handing the
// visitor to Braindate is the whole purpose of the screen, and Figma
// 5978:152505 draws exactly that — a redirect notice and a loader, no button.
//
// The delay is deliberate rather than incidental: the visitor is being sent off
// UNBOUND, so the screen needs to be readable before it goes.
const REDIRECT_DELAY_MS = 2000;
let redirectTimer = null;

// `replace`, not `assign`: Back should return the visitor to wherever they came
// from, not to this page, which would immediately forward them again.
const goToBraindate = () => {
    if (!href.value || typeof window === 'undefined') return;
    window.location.replace(href.value);
};

onMounted(() => {
    // Watched rather than fired once: on a direct load the session resolves
    // after mount, so `href` is null for the first tick or two. The timer guard
    // is what makes this fire once — NOT a self-stopping watcher, which cannot
    // work here: `immediate` runs the callback synchronously while the handle is
    // still being assigned, so referencing it inside throws.
    watch(href, (value) => {
        if (!value || redirectTimer) return;
        redirectTimer = setTimeout(goToBraindate, REDIRECT_DELAY_MS);
    }, { immediate: true });
});

onBeforeUnmount(() => {
    if (redirectTimer) clearTimeout(redirectTimer);
});
</script>
<template>
    <!-- 2026 styling, per Figma 5978:152505. What was here before was the 2025
         INBOUND treatment: teal, rem-scaled type and a hand-rolled button, none
         of which exists in the 2026 system. Values come from the comp's own
         Container — 45px between the message and the loader, 20px inside the
         message, 30px/1.3 serif headline, 15px/23px body.
         The comp draws this inside the modal shell, which supplies a 110px
         gutter; this renders as a page instead (pages/braindate.vue centres it),
         so it takes a max-width rather than that fixed padding. -->
    <div class="braindate-modal flex flex-col items-center gap-[45px] w-full max-w-[681px] mx-auto text-center text-(--magenta)">
        <div class="flex flex-col items-center gap-[20px] w-full">
            <h1 class="m-0 w-full font-hubspot-serif font-normal text-[25px] sm:text-[30px] leading-[1.3]">Redirecting you to Braindate</h1>
            <p class="m-0 w-full font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px]">
                Create, join and host 1-on-1 and group conversations at UNBOUND.
            </p>
        </div>

        <FragmentsLoading loading class="w-[59px] h-[55px]" />

        <!-- Fallback, not the primary path. The redirect above is what normally
             takes the visitor there; this covers a blocked navigation or a
             session that resolves unusually late, so nobody is stranded on a
             loader with no way forward. -->
        <a
            v-if="href"
            :href="href"
            :class="['braindate-modal__cta', modal_button_outline_2026]"
        >
            Open Braindate
        </a>
    </div>
</template>
